import React from 'react';
import {
  FaNetworkWired,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaServer,
} from 'react-icons/fa';
import { VoIPStats } from '../utils/voipTypes';

interface VoIPStatCardsProps {
  stats: VoIPStats;
}

const VoIPStatCards: React.FC<VoIPStatCardsProps> = ({ stats }) => {
  // Calculate percentage for reachability
  const reachabilityPercent = stats.totalDevices > 0
    ? Math.round((stats.onlineDevices / stats.totalDevices) * 100)
    : 0;

  // Only show cards with actual data available from API
  const cards = [
    {
      id: 'total',
      icon: <FaServer />,
      value: stats.totalDevices,
      label: 'Total Devices',
      trend: null,
      color: '#0056b3',
    },
    {
      id: 'reachable',
      icon: <FaCheckCircle />,
      value: stats.onlineDevices,
      label: 'Reachable',
      trend: stats.totalDevices > 0 ? `${reachabilityPercent}%` : null,
      trendType: 'up' as const,
      color: '#28a745',
    },
    {
      id: 'unreachable',
      icon: <FaTimesCircle />,
      value: stats.offlineDevices,
      label: 'Unreachable',
      trend: stats.offlineDevices > 0 ? 'Alert' : null,
      trendType: stats.offlineDevices > 0 ? 'down' as const : undefined,
      color: '#dc3545',
    },
    {
      id: 'network',
      icon: <FaNetworkWired />,
      value: `${reachabilityPercent}%`,
      label: 'Network Health',
      trend: reachabilityPercent >= 95 ? 'Good' : reachabilityPercent >= 80 ? 'Fair' : 'Poor',
      trendType: reachabilityPercent >= 95 ? 'up' as const : reachabilityPercent >= 80 ? 'warning' as const : 'down' as const,
      color: reachabilityPercent >= 95 ? '#28a745' : reachabilityPercent >= 80 ? '#ffc107' : '#dc3545',
    },
    {
      id: 'alerts',
      icon: <FaExclamationTriangle />,
      value: stats.activeAlerts,
      label: 'Active Alerts',
      trend: stats.activeAlerts > 0 ? 'Action Required' : null,
      trendType: stats.activeAlerts > 0 ? 'warning' as const : undefined,
      color: stats.activeAlerts > 0 ? '#dc3545' : '#6c757d',
    },
  ];

  return (
    <div className="voip-dashboard-stats">
      {cards.map((card) => (
        <div key={card.id} className="voip-stat-card" style={{ '--card-color': card.color } as React.CSSProperties}>
          <div className="stat-header">
            <div className="stat-icon" style={{ color: card.color, background: `${card.color}15` }}>
              {card.icon}
            </div>
            {card.trend && (
              <span className={`stat-trend ${card.trendType || ''}`}>{card.trend}</span>
            )}
          </div>
          <div className="stat-value">{card.value}</div>
          <div className="stat-label">{card.label}</div>
        </div>
      ))}
    </div>
  );
};

export default VoIPStatCards;
